import React from 'react';
import { Badge, Button, Col, Form, Row } from 'react-bootstrap';
import { Branch } from 'dorian-shared/types/branch/Branch';
import { MemoryDTO } from '../../../../Book/MemoryBank/memoryBankTypes';
import { AddStepGotoBranchSelect } from '../../AddStepGotoBranchSelect';
import { IntValueOrVariableSelect } from './IntValueOrVariableSelect';
import './IntValueOrVariableSelect.scss';

type ConditionListItemFormProps = {
  stepIndex: number,
  switchIndex: number,
  gotoBranchId: number,
  currentBranchId: number,
  branches: Branch[],
  conditionNumber: number,
  isLastRow: boolean,
  totalConditions: number,
  variableName: string,
  operator?: string,
  intValue?: string | number,
  varValue?: number,
  variables: MemoryDTO[],
  leftOperandVariableId?: number, // The main variable to exclude
  handleChangeStepSwitch: (gotoBranchId: number, stepIndex: number, switchIndex: number) => void,
  handleChangeCondition?: (operator: string, intValue: string, stepIndex: number, switchIndex: number) => void,
  handleChangeVariableCondition?: (operator: string, varValue: number, stepIndex: number, switchIndex: number) => void,
  handleRemoveCondition?: (stepIndex: number, switchIndex: number) => void,
  isDisabled?: boolean,
}

export function ConditionListItemForm(props: ConditionListItemFormProps) {
  const {
    stepIndex, switchIndex, gotoBranchId, currentBranchId, handleChangeStepSwitch, branches,
    isDisabled, conditionNumber, isLastRow, totalConditions, variableName, operator, intValue,
    varValue, variables, leftOperandVariableId, handleChangeCondition, handleChangeVariableCondition, handleRemoveCondition,
  } = props;

  const gotoBranchName = `steps[${stepIndex}][switch][${switchIndex}]`;
  
  const handleOperatorChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newOperator = event.target.value; // Will be 'equal', 'less', or 'greater'
    // Preserve current value state (either intValue or varValue)
    if (varValue !== undefined && varValue !== null && handleChangeVariableCondition) {
      handleChangeVariableCondition(newOperator, varValue, stepIndex, switchIndex);
    } else if (handleChangeCondition) {
      handleChangeCondition(newOperator, String(intValue || ''), stepIndex, switchIndex);
    }
  };
  
  const handleValueChange = (data: { intValue?: string, varValue?: number }) => {
    const currentOperator = operator || 'equal';
    if (data.varValue !== undefined && handleChangeVariableCondition) {
      // Switch to variable mode
      handleChangeVariableCondition(currentOperator, data.varValue, stepIndex, switchIndex);
    } else if (data.intValue !== undefined && handleChangeCondition) {
      // Switch to number mode
      handleChangeCondition(currentOperator, data.intValue, stepIndex, switchIndex);
    }
  };
  
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
    ];
    
    const key = event.key;
    
    // Allow keyboard shortcuts with Cmd (Mac) or Ctrl (Windows/Linux)
    // This includes paste (Cmd+V/Ctrl+V), copy (Cmd+C/Ctrl+C), cut (Cmd+X/Ctrl+X), select all (Cmd+A/Ctrl+A)
    if (event.metaKey || event.ctrlKey) {
      return;
    }
    
    // Allow control/navigation keys
    if (allowedKeys.includes(key)) {
      return;
    }
    
    // Allow numbers 0-9
    if (key >= '0' && key <= '9') {
      return;
    }
    
    // Handle minus sign - replace entire value with minus
    if (key === '-') {
      event.preventDefault();
      if (handleChangeCondition) {
        handleChangeCondition(operator || 'equal', '-', stepIndex, switchIndex);
      }
      return;
    }
    
    // Block all other keys (including period)
    event.preventDefault();
  };
  
  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text').trim();
    
    // Check if it's a valid integer (positive or negative, no decimals)
    const isValidInteger = /^-?\d+$/.test(pastedText);
    
    let finalValue: string;
    if (isValidInteger) {
      // Valid integer - use as-is (after trimming)
      finalValue = pastedText;
    } else {
      // Try to parse as integer (handles cases like "123.45" -> "123")
      const parsedValue = parseInt(pastedText, 10);
      finalValue = isNaN(parsedValue) ? '0' : String(parsedValue);
    }
    
    // Cap at 15 characters
    if (finalValue.length > 15) {
      finalValue = finalValue.substring(0, 15);
    }
    
    if (handleChangeCondition) {
      handleChangeCondition(operator || 'equal', finalValue, stepIndex, switchIndex);
    }
  };
  
  const handleRemove = () => {
    if (handleRemoveCondition) {
      handleRemoveCondition(stepIndex, switchIndex);
    }
  };
  
  // Show X button only if there are 3+ conditions and this is not the last row
  const showRemoveButton = totalConditions >= 3 && !isLastRow;

  return (
    <Col>
      <Form.Group as={Row} className="mb-0">
        {!isLastRow ? (
          <>
            <Col xs="auto" className="d-flex align-items-center pr-1">
              <Form.Label className="mb-0" style={{ fontSize: '0.9rem', marginRight: '0.25rem' }}>If</Form.Label>
              <Badge variant="info" style={{ fontSize: '0.875rem' }}>{variableName}</Badge>
            </Col>
            <Col xs={3} sm={2} className="pr-1" style={{ minWidth: '118px', maxWidth: '120px' }}>
              <Form.Control
                disabled={isDisabled}
                size="sm"
                as="select"
                value={operator || 'equal'}
                onChange={handleOperatorChange}
                name={`${gotoBranchName}[operator]`}
              >
                <option value="equal">Exactly</option>
                <option value="notEqual">Not exactly</option>
                <option value="less">Less than</option>
                <option value="atMost">At most</option>
                <option value="greater">More than</option>
                <option value="atLeast">At least</option>
              </Form.Control>
            </Col>
            <Col xs={3} sm={3} md={3} className="pr-1" style={{ minWidth: '120px' }}>
              <IntValueOrVariableSelect
                intValue={intValue}
                varValue={varValue}
                variables={variables}
                excludeVariableId={leftOperandVariableId}
                onChange={handleValueChange}
                disabled={isDisabled}
                stepIndex={stepIndex}
                switchIndex={switchIndex}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
              />
              {/* Hidden inputs for form serialization */}
              {varValue ? (
                <input type="hidden" name={`${gotoBranchName}[variable]`} value={varValue} />
              ) : (
                <input type="hidden" name={`${gotoBranchName}[number]`} value={intValue || ''} />
              )}
            </Col>
            <Col xs="auto" className="d-flex align-items-center pr-1">
              <Form.Label className="mb-0" style={{ fontSize: '0.9rem' }}>goto branch</Form.Label>
            </Col>
            <Col xs={12} sm={12} md={5} lg={4} className="pr-1">
              <div className={!gotoBranchId || gotoBranchId === 0 ? 'bg-warning-light' : ''}>
                <AddStepGotoBranchSelect
                  inputName={`${gotoBranchName}[gotoBranchId]`}
                  gotoBranchId={gotoBranchId}
                  isDisabled={isDisabled}
                  currentBranchId={currentBranchId}
                  branches={branches}
                  onBranchChange={(selectedGotoBranchId) => {
                    const gotoBranchIdValue = selectedGotoBranchId ? Number(selectedGotoBranchId) : 0;
                    handleChangeStepSwitch(gotoBranchIdValue, stepIndex, switchIndex);
                  }}
                />
              </div>
            </Col>
            {showRemoveButton && (
              <Col xs="auto" className="d-flex align-items-center">
                <Button
                  className="btnDeleteImgSm"
                  size="sm"
                  variant="secondary"
                  onClick={handleRemove}
                  disabled={isDisabled}
                  title="Remove condition"
                  onMouseDown={(e) => e.stopPropagation()}
                />
              </Col>
            )}
          </>
        ) : (
          <>
            <Col xs="auto" className="d-flex align-items-center pr-2">
              <Form.Label className="mb-0" style={{ fontSize: '0.9rem' }}>Otherwise goto branch</Form.Label>
            </Col>
            <Col xs={12} sm={12} md={6} >
              <div className={!gotoBranchId || gotoBranchId === 0 ? 'bg-warning-light' : ''}>
                <AddStepGotoBranchSelect
                  inputName={`${gotoBranchName}[gotoBranchId]`}
                  gotoBranchId={gotoBranchId}
                  isDisabled={isDisabled}
                  currentBranchId={currentBranchId}
                  branches={branches}
                  onBranchChange={(selectedGotoBranchId) => {
                    const gotoBranchIdValue = selectedGotoBranchId ? Number(selectedGotoBranchId) : 0;
                    handleChangeStepSwitch(gotoBranchIdValue, stepIndex, switchIndex);
                  }}
                />
              </div>
            </Col>
          </>
        )}
      </Form.Group>
    </Col>
  );
}

